<template>
  <form
    class="flex w-full flex-col gap-6 p-4"
    novalidate
    @submit.prevent="handleSubmit"
  >
    <BaseFormErrorSummary
      :errors="errors"
      :submit-count="submitCount"
    />

    <BaseInlineNotification
      v-if="submissionError"
      heading-text="An Error Occurred"
      hide-title-element
      type="error"
    >
      <p
        class="body-sm"
        v-text="submissionError"
      />
    </BaseInlineNotification>

    <BaseInlineNotification
      v-if="hasRecaptchaError"
      heading-text="reCAPTCHA Verification Failed"
      hide-title-element
      type="error"
    >
      <p
        class="body-sm"
        v-text="'Security verification failed. Please try again.'"
      />
    </BaseInlineNotification>
    
    <BaseTextInput
      id="email"
      v-model="values.email"
      autocomplete="email"
      label="Email"
      name="email"
      required
      type="email"
      validation-rules="required|email"
    />

    <BaseButton
      :disabled="isSubmitting || isValidatingRecaptcha"
      class="btn--primary"
      text="Submit"
      type="submit"
    />

    <BaseRecaptchaNotice />
  </form>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import { useForm } from 'vee-validate'

import BaseButton from '@js/components/global/BaseButton.vue'
import BaseTextInput from '@js/components/global/forms/inputs/BaseTextInput.vue'
import BaseInlineNotification from '@js/components/global/BaseInlineNotification.vue'
import BaseFormErrorSummary from '@js/components/global/forms/BaseFormErrorSummary.vue'
import BaseRecaptchaNotice from '../../global/forms/BaseRecaptchaNotice.vue'

import { log } from '@js/utils/logger'
import useRecaptcha from '@js/composables/useRecaptcha'
import { forgotPassword } from '@js/api/auth/forgotPassword'

defineOptions({
  name: 'ForgotPasswordForm',
})

const emit = defineEmits<{
  showMessage: [void]
}>()

const { values,
  errors,
  resetForm,
  submitCount,
  isSubmitting,
  handleSubmit: handleValidationSubmit,
  setFieldValue,
} = useForm({
  initialValues: {
    email: '',
  },
})

const { loadRecaptcha, getRecaptchaToken, validateRecaptcha, isValidatingRecaptcha } = useRecaptcha()

const submissionError = ref('')
const hasRecaptchaError = ref(false)

const handleSubmit = handleValidationSubmit(async (): Promise<void> => {
  try {
    submissionError.value = ''
    hasRecaptchaError.value = false

    const token = await getRecaptchaToken('forgot_password')
    if (!token) {
      hasRecaptchaError.value = true
      return
    }

    await forgotPassword(values.email, token)

    emit('showMessage')
  } catch (error) {
    submissionError.value = 'An error occurred. Please try again.'
    log({
      message: 'Forgot Password Error',
      file: 'src/wcei/js/components/auth/forgot-password/ForgotPasswordForm.vue',
    })
  } finally {
    resetForm()
  }
})

onMounted(() => {
  loadRecaptcha()
  
  const params = new URLSearchParams(window.location.search)
  const emailFromUrl = params.get('email')

  if (emailFromUrl) {
    setFieldValue('email', emailFromUrl)
  }
})
</script>
